// Cry detector for the baby monitor. Give it a MediaStream plus callbacks and it
// watches the microphone for the acoustic signature of a crying baby, firing
// `onCry` (which the host turns into a Web Push) and then rate-limiting itself.
//
// This is deliberately *not* a plain loudness gate. A volume threshold fires on
// doors, fans, TV and passing traffic while missing a muffled cry, which makes
// alerts feel random. Instead each frame is judged on three cheap signals taken
// from a single FFT:
//
//   1. Band energy — infant cries put most of their energy around 250-800 Hz, so
//      we measure that band rather than the whole spectrum. Rumble (HVAC, traffic)
//      and hiss (taps, static) land outside it and no longer count as "loud".
//   2. Adaptive floor — the trigger level tracks the room's own ambient level, so
//      the same code works in a quiet nursery and next to a noisy street without
//      a per-room sensitivity setting. Frames that look like a cry are excluded
//      from the floor, so a long wail can never teach the room to ignore it.
//   3. Shape — the band has to hold a meaningful share of the total energy, the
//      spectral centroid has to sit in a voice-like range, and the level has to
//      wobble. Steady in-band machine noise stays flat; a cry rises and falls.
//
// A cry then has to win a majority of frames in a ~2 s window (rather than an
// unbroken run), which tolerates the pauses between wails while still ignoring
// one-off bangs.
//
// `onLevel(level, trigger)` is called every frame so a UI can draw a live meter.

// A single shared AudioContext for all cry detectors. It must be created and
// resumed from a genuine user gesture (see unlockCryAudio), because iOS Safari
// leaves a context created outside activation in the 'suspended' state — and a
// suspended context never advances the analyser, so the host's mic is never
// examined and cry alerts silently never fire. The host's detector attaches its
// stream long after the tap (behind the async ICE fetch + peer open), so the
// gesture-time unlock is what keeps detection alive. Shared so we don't leak a
// context per detector either.
let sharedCtx = null;

function getSharedCtx() {
  if (sharedCtx) return sharedCtx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  try {
    sharedCtx = new Ctx();
  } catch (e) {
    sharedCtx = null;
  }
  return sharedCtx;
}

// Call from a real user gesture (choosing the "baby monitor" role) to create +
// resume the shared context while activation is live. Safe/no-op if the Web
// Audio API is unavailable. Returns the context (or null) for callers that care.
export function unlockCryAudio() {
  const ctx = getSharedCtx();
  if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
}

// The cry band itself, and the wider band we compare it against. The context
// band stops at 2.5 kHz because everything above it is hiss as far as this
// decision goes, and starts at 60 Hz to drop mains hum and handling thumps.
const CRY_LO_HZ = 250;
const CRY_HI_HZ = 800;
const CTX_LO_HZ = 60;
const CTX_HI_HZ = 2500;

// Decision window: 20 frames at ~10 Hz ≈ 2 s of history.
const WINDOW = 20;

// How fast the ambient floor follows the room. Downwards it moves quickly (the
// room really did go quiet); upwards it crawls, so a new steady noise is
// absorbed over ~10 s while a short cry never drags the floor up behind it.
const ADAPT_DOWN = 0.3;
const ADAPT_UP = 0.01;

export function createCryDetector(options = {}) {
  const opts = {
    // Absolute floor. Purely a "is there any real sound at all" guard so that in
    // a near-silent room the relative test can't promote faint rustling into a
    // cry. Band amplitudes run ~0.0003 (silent room) through ~0.007 (a muffled
    // cry heard across the room) to ~0.15 (a baby crying next to the phone), so
    // this sits well under a faint cry while staying far above room noise. In any
    // room louder than that, the adaptive gates below are what actually decide.
    minLevel: 0.003,
    // Relative gates: the band has to clear both a multiple of ambient and a
    // margin above its usual variation before the frame counts.
    floorMult: 2.5,
    devMult: 4,
    // Share of 60-2500 Hz energy that must sit inside the cry band.
    minRatio: 0.35,
    // Voice-like brightness window for the spectral centroid, in Hz.
    centroidLoHz: 180,
    centroidHiHz: 2200,
    // Peak-to-trough variation required across the window, as a fraction of the
    // peak. Rejects flat, unchanging in-band noise. Waived once the band is this
    // many times over the trigger, so a full-throated sustained scream — loud and
    // genuinely steady — still gets through.
    minModulation: 0.12,
    flatBypassMult: 2.5,
    // Frames of the window that must look like a cry before we fire.
    minCryFrames: 10,
    // Ignore the first moments after the mic opens while the floor is learned.
    warmupMs: 2000,
    // Quiet period after an alert. The shorter repeat applies while the room
    // still sounds like an actively crying baby, so ongoing distress isn't
    // silenced for the full cooldown.
    cooldownMs: 30000,
    repeatCooldownMs: 15000,
    ongoingRatio: 0.7,
    onCry: null,
    onLevel: null,
    ...options,
  };

  let sourceNode = null;
  let analyser = null;
  let freq = null;
  let rafId = 0;
  let lastFrame = 0;

  let stream = null;
  let running = false;

  // Bin geometry, derived once per analyser from the context's sample rate.
  let binHz = 0;
  let ctxLoBin = 0;
  let ctxHiBin = 0;
  let cryLoBin = 0;
  let cryHiBin = 0;

  // Ambient model and decision window. These deliberately survive re-wiring: a
  // camera switch or reconnect shouldn't throw away what we know about the room
  // and blind us mid-cry.
  let floor = 0;
  let dev = 0;
  let calibrated = false;
  let calibratedAt = 0;
  const histCry = new Uint8Array(WINDOW);
  const histLevel = new Float32Array(WINDOW);
  let histAt = 0;
  let histLen = 0;
  let cryFrames = 0;
  let lastTrigger = 0;

  let lastCry = 0;

  function attach(mediaStream) {
    stream = mediaStream;
    if (running) wire();
  }

  // Sets the absolute minimum band level. The room-relative part of the decision
  // adapts on its own, so this only shifts the "any sound at all" guard.
  function setThreshold(t) {
    opts.minLevel = t;
  }

  function start() {
    running = true;
    wire();
  }

  function stop() {
    running = false;
    if (rafId) cancelAnimationFrame(rafId);
    rafId = 0;
    if (sourceNode) {
      try {
        sourceNode.disconnect();
      } catch (e) {
        /* already disconnected */
      }
    }
    if (opts.onLevel) opts.onLevel(0, opts.minLevel);
  }

  function wire() {
    if (!stream) return;

    const ctx = getSharedCtx();
    if (!ctx) return;
    // Resume every time we wire: the context can slip back to 'suspended' after
    // the tab is backgrounded, and a suspended context silently stops analysis.
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});

    if (!analyser) {
      analyser = ctx.createAnalyser();
      // 2048 gives ~23 Hz bins at 48 kHz, enough to resolve the cry band. The
      // built-in smoothing is dialled back because the window below does our
      // time-domain reasoning and we want each frame to be current.
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.3;
      freq = new Float32Array(analyser.frequencyBinCount);

      binHz = ctx.sampleRate / analyser.fftSize;
      const lastBin = analyser.frequencyBinCount - 1;
      const bin = (hz) => Math.min(lastBin, Math.max(0, Math.round(hz / binHz)));
      ctxLoBin = bin(CTX_LO_HZ);
      ctxHiBin = bin(CTX_HI_HZ);
      cryLoBin = bin(CRY_LO_HZ);
      cryHiBin = bin(CRY_HI_HZ);
    }

    // Re-point at the (possibly new, post-reconnect) stream. The analyser is
    // never connected to `destination`, so this taps the audio without echoing
    // it back out.
    if (sourceNode) {
      try {
        sourceNode.disconnect();
      } catch (e) {
        /* already disconnected */
      }
    }
    try {
      sourceNode = ctx.createMediaStreamSource(stream);
      sourceNode.connect(analyser);
    } catch (e) {
      return;
    }

    loop();
  }

  // Pull one frame of spectrum and reduce it to the three signals we judge on.
  function measure() {
    analyser.getFloatFrequencyData(freq);

    let cryPow = 0;
    let totalPow = 0;
    let weighted = 0;

    for (let i = ctxLoBin; i <= ctxHiBin; i++) {
      // getFloatFrequencyData reports dB relative to full scale; silence comes
      // back as -Infinity, which converts to a harmless 0.
      const p = Math.pow(10, freq[i] / 10);
      totalPow += p;
      weighted += i * binHz * p;
      if (i >= cryLoBin && i <= cryHiBin) cryPow += p;
    }

    return {
      level: Math.sqrt(cryPow),
      ratio: totalPow > 0 ? cryPow / totalPow : 0,
      centroid: totalPow > 0 ? weighted / totalPow : 0,
    };
  }

  function pushHistory(cryish, level) {
    if (histLen === WINDOW) {
      cryFrames -= histCry[histAt];
    } else {
      histLen++;
    }
    histCry[histAt] = cryish ? 1 : 0;
    histLevel[histAt] = level;
    cryFrames += cryish ? 1 : 0;
    histAt = (histAt + 1) % WINDOW;
  }

  // Loudest frame in the window, plus its peak-to-trough spread as a fraction of
  // that peak.
  function windowStats() {
    let min = Infinity;
    let max = 0;
    for (let i = 0; i < histLen; i++) {
      const v = histLevel[i];
      if (v < min) min = v;
      if (v > max) max = v;
    }
    return { peak: max, range: max > 0 ? (max - min) / max : 0 };
  }

  function loop() {
    if (rafId) return;

    const tick = (now) => {
      rafId = requestAnimationFrame(tick);
      if (now - lastFrame < 100) return; // ~10 Hz is plenty, easy on battery
      lastFrame = now;
      if (!analyser) return;

      const { level, ratio, centroid } = measure();

      // Seed the ambient model from the first frame we ever see, so the floor
      // starts somewhere sane instead of climbing from zero.
      if (!calibrated) {
        calibrated = true;
        floor = level;
        dev = level * 0.5;
        calibratedAt = now;
      }

      const trigger = Math.max(opts.minLevel, floor * opts.floorMult, floor + opts.devMult * dev);
      lastTrigger = trigger;
      const cryish =
        level >= trigger &&
        ratio >= opts.minRatio &&
        centroid >= opts.centroidLoHz &&
        centroid <= opts.centroidHiHz;

      // Only quiet, non-cry frames teach the ambient model. Excluding cry-like
      // frames is what stops a long wail from being normalised into the floor.
      if (!cryish) {
        const rate = level < floor ? ADAPT_DOWN : ADAPT_UP;
        floor += (level - floor) * rate;
        dev += (Math.abs(level - floor) - dev) * rate;
      }

      pushHistory(cryish, level);
      if (opts.onLevel) opts.onLevel(level, trigger);
      detect(now);
    };

    rafId = requestAnimationFrame(tick);
  }

  function detect(now) {
    if (histLen < WINDOW) return;
    if (now - calibratedAt < opts.warmupMs) return;
    if (cryFrames < opts.minCryFrames) return;

    const { peak, range } = windowStats();
    if (range < opts.minModulation && peak < lastTrigger * opts.flatBypassMult) return;

    // Still mostly cry-like? Treat this as the same episode continuing and allow
    // the shorter repeat interval rather than the full cooldown.
    const ongoing = cryFrames / WINDOW >= opts.ongoingRatio;
    const cooldown = ongoing ? opts.repeatCooldownMs : opts.cooldownMs;
    if (now - lastCry < cooldown) return;

    lastCry = now;
    if (opts.onCry) opts.onCry();
  }

  function resume() {
    if (running) wire();
  }

  return { attach, start, stop, setThreshold, resume };
}
